// TASK-10076 — a tab opened before a deploy must not dead-end on a lazy screen.
//
// CASE (TITS, 2026-10-25): five "[HR] Failed to fetch dynamically imported module
// …/dist/chunks/chunk-OOS36O4Z.js" errors an hour after v1.15.2119.0 went out. A
// tab loaded on the old build clicked into a lazy HR screen. Every build names its
// chunks by content hash and a deploy ships only the new set, so the old chunk is
// gone (hosting answers the old URL with the HTML fallback, 200, and the import
// fails). HR said "Check your connection and try again" — advice that can never
// work on that tab.
//
// WHAT THIS DOES: scripts/build-frontend.cjs (dd-chunk-reload plugin) wraps every
// relative dynamic import() in public/src as __ddChunkWrap(import(…)), which hands
// the promise to window.__ddChunk below. Success passes straight through. On a
// failure that looks like a missing chunk we ask the server which version it
// serves:
//   - served !== the version THIS tab runs → the tab is stale. Office tab with
//     nothing unsaved: reload once onto the new build (the promise stays pending,
//     so the screen's own error never shows). Unsaved work, an open dialog, a
//     driver session, or a reload already tried for this version: ask first ("DispatchDeck was
//     updated"), Reload / Not now.
//   - offline, version check fails, or served === running → not a stale tab. The
//     error goes back to the screen untouched, so its own connection message still
//     shows (that message is right for those cases).
//
// LOOP-PROOF: the automatic reload is recorded in sessionStorage against the
// served version. A second failure for that same version within 10 minutes never
// reloads by itself again; it asks. Storage unusable → always ask. A new deploy
// (different served version) earns one more automatic reload.
//
// TASK-10108 — BAD COPY IN THE BROWSER CACHE (same TITS case, the next day). While
// a chunk is missing, hosting answers its URL with the HTML fallback, 200, and
// "Cache-Control: public, max-age=86400" (the **/*.js header rule matches the
// request path). The browser keeps that HTML under the chunk URL for 24 hours. A
// later deploy put chunk-OOS36O4Z.js back, but Jess's browser kept loading its
// cached HTML: every "View MVR" failed for a day, on a CURRENT tab (served ===
// running), across browser restarts, while it opened fine for everyone else. A
// plain reload does not help (the cache entry is still fresh), so when served ===
// running we re-download the chunk with cache: 'reload', which goes to the server
// AND replaces the cached copy. Real JavaScript back → the copy was bad; this
// page keeps the failed import for its lifetime, so reload once (same guards as
// above; a second failure for that chunk within 10 minutes just shows the screen's
// own error). HTML or an error back → the chunk is really missing on this build;
// the error goes to the screen as before. Needs the chunk URL in the message
// (Chrome, Edge, Firefox; Safari's message has none). firebase.json now sends
// /dist/chunks/** with no-cache, so a fallback can't be cached again.
//
// One place, every lazy screen: the sites keep their own catch/toast code and
// need no edits. Covered = every literal relative import() in public/src under
// the /dist build (tests/task10076-chunk-reload.unit.cjs fails if a new import()
// shape slips past the plugin). Not covered: source mode (/src served raw has no
// hashed chunks to lose) and gstatic CDN imports (not our chunks).

(function initChunkReload(root) {
  'use strict';
  if (!root) return;

  // Chrome/Edge · Firefox · Safari · MIME refusals of the HTML fallback.
  const CHUNK_ERR_RE = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Expected a JavaScript(?:-or-Wasm)? module script|not a valid JavaScript MIME type/i;
  const FLAG_KEY = 'dd.staleChunkReload';
  // TASK-10108 — heal attempts, one timestamp per chunk URL (its own key, so a
  // stale-tab reload can never overwrite it and A→B→A can't dodge the guard).
  const HEAL_KEY = 'dd.badChunkHeal';
  const FLAG_TTL_MS = 10 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 4000;
  const DECLINE_QUIET_MS = 60 * 1000;
  // A reload that was promised but never happened (navigation blocked, storage
  // threw inside applyUpdate…) must not leave the screen waiting forever.
  const RELOAD_WATCHDOG_MS = 15000;
  const VERSION_RE = /^\d+(\.\d+)+$/;
  // TASK-10108 — the chunk URL inside the browser's message (Chrome/Edge/Firefox).
  const CHUNK_URL_RE = /(https?:\/\/[^\s'"()<>]+?\/dist\/chunks\/[A-Za-z0-9_-]+\.js)/;
  const JS_TYPE_RE = /javascript|ecmascript/i;

  const state = { reloading: false, prompting: null, declinedAt: 0 };
  let deps = null;

  function isChunkLoadError(err) {
    const msg = err && (err.message || (typeof err === 'string' ? err : ''));
    return !!msg && CHUNK_ERR_RE.test(String(msg));
  }

  async function fetchServedVersion() {
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS) : null;
    try {
      const res = await root.fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store', ...(ctrl ? { signal: ctrl.signal } : {}),
      });
      if (!res || !res.ok) throw new Error('version.json HTTP ' + (res ? res.status : '?'));
      const v = (await res.json())?.version;
      if (!v || !VERSION_RE.test(String(v))) throw new Error('version.json has no usable version');
      return String(v);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // TASK-10108 — the failing chunk's URL, only when it is one of OUR chunks on
  // this page's own origin (never fetch an arbitrary URL out of an error message).
  function chunkUrlOf(err) {
    const msg = String((err && (err.message || (typeof err === 'string' ? err : ''))) || '');
    const m = CHUNK_URL_RE.exec(msg);
    if (!m) return null;
    let origin = null;
    try { origin = root.location && root.location.origin; } catch (_) { origin = null; }
    if (!origin) return null;
    try {
      const u = new URL(m[1]);
      return u.origin === origin ? u.href : null;
    } catch (_) { return null; }
  }

  // TASK-10108 — download the chunk from the server, replacing whatever the browser
  // cached under that URL. true = the server has real JavaScript there now.
  async function refetchChunk(url) {
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS) : null;
    try {
      const res = await root.fetch(url, {
        cache: 'reload', credentials: 'same-origin', ...(ctrl ? { signal: ctrl.signal } : {}),
      });
      if (!res || !res.ok) return false;
      const type = (res.headers && typeof res.headers.get === 'function' && res.headers.get('content-type')) || '';
      if (!JS_TYPE_RE.test(String(type))) return false;
      // Read the body so the fresh copy is fully stored before the reload (COD).
      if (typeof res.arrayBuffer === 'function') await res.arrayBuffer();
      return true;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // null = no flag; undefined = storage unusable (then we can't prove loop-safety).
  function readFlag() {
    try {
      const raw = root.sessionStorage.getItem(FLAG_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return undefined; }
  }
  function writeFlag(served) {
    try { root.sessionStorage.setItem(FLAG_KEY, JSON.stringify({ to: served, at: Date.now() })); return true; } catch (_) { return false; }
  }

  // TASK-10108 — {url: at} of heal reloads; undefined = storage unusable.
  function readHeals() {
    try {
      const raw = root.sessionStorage.getItem(HEAL_KEY);
      const v = raw ? JSON.parse(raw) : {};
      return v && typeof v === 'object' ? v : {};
    } catch (_) { return undefined; }
  }
  function writeHeal(url) {
    const now = Date.now();
    const v = readHeals() || {};
    for (const k of Object.keys(v)) if (!(now - Number(v[k] || 0) < FLAG_TTL_MS)) delete v[k];
    v[url] = now;
    try { root.sessionStorage.setItem(HEAL_KEY, JSON.stringify(v)); return true; } catch (_) { return false; }
  }

  // TASK-10108 — served === running. Was it a bad cached copy of the chunk?
  async function decideCurrent(err, served, running) {
    const url = chunkUrlOf(err);
    if (!url) return { action: 'rethrow', why: 'current', served, running };
    let ok = false;
    try { ok = await refetchChunk(url); } catch (_) { return { action: 'rethrow', why: 'refetch-failed', served, running }; }
    // Still missing on the server: reloading would not help.
    if (!ok) return { action: 'rethrow', why: 'current', served, running };
    const heals = readHeals();
    if (heals && Date.now() - Number(heals[url] || 0) < FLAG_TTL_MS) {
      return { action: 'rethrow', why: 'heal-already-tried', served, running, kind: 'heal' };
    }
    let why = null;
    if (heals === undefined) why = 'no-storage';
    else if (deps.isDriverSession && deps.isDriverSession()) why = 'driver';
    else if (deps.hasUnsavedWork && deps.hasUnsavedWork()) why = 'unsaved';
    else if (deps.isModalOpen && deps.isModalOpen()) why = 'modal-open';
    return why
      ? { action: 'prompt', why, served, running, kind: 'heal', url }
      : { action: 'reload', why: 'bad-cached-chunk', served, running, kind: 'heal', url };
  }

  // → { action: 'rethrow' | 'reload' | 'prompt', why, served, running }
  async function decide(err) {
    if (!deps || !isChunkLoadError(err)) return { action: 'rethrow', why: 'not-a-chunk-error' };
    if (root.navigator && root.navigator.onLine === false) return { action: 'rethrow', why: 'offline' };
    const running = deps.getRunningVersion ? deps.getRunningVersion() : null;
    if (!running) return { action: 'rethrow', why: 'no-running-version' };
    let served;
    try { served = await fetchServedVersion(); } catch (_) { return { action: 'rethrow', why: 'version-check-failed' }; }
    // Same build: the chunk should exist. A bad copy in the browser cache gets one
    // re-download + reload (TASK-10108); a blip or a broken deploy — reloading
    // would not help, and must not loop. Let the screen say what it says.
    if (served === running) return decideCurrent(err, served, running);
    const flag = readFlag();
    const tried = !!(flag && flag.to === served && Date.now() - (flag.at || 0) < FLAG_TTL_MS);
    let why = null;
    if (flag === undefined) why = 'no-storage';
    else if (tried) why = 'already-reloaded';
    else if (deps.isDriverSession && deps.isDriverSession()) why = 'driver';
    else if (deps.hasUnsavedWork && deps.hasUnsavedWork()) why = 'unsaved';
    else if (deps.isModalOpen && deps.isModalOpen()) why = 'modal-open';
    return why ? { action: 'prompt', why, served, running } : { action: 'reload', why: 'stale', served, running };
  }

  function log(d) {
    try {
      root.Telemetry?.logUserActionOnce?.('stale_chunk_' + d.action, 'stale_chunk', {
        action: d.action, why: d.why, running: d.running, served: d.served, kind: d.kind || 'stale',
      });
    } catch (_) {}
  }

  // Held while the page reloads. If the page is still here after the watchdog,
  // the reload did not happen: let go and hand the screen its original error.
  function holdForReload(err) {
    return new Promise((_, reject) => {
      setTimeout(() => { state.reloading = false; reject(err); }, (deps && deps.reloadWatchdogMs) || RELOAD_WATCHDOG_MS);
    });
  }

  async function ask(d) {
    const heal = d.kind === 'heal';
    if (typeof deps.showConfirm !== 'function') {
      deps.showToast?.(heal ? 'Refresh the page to open this screen.' : 'DispatchDeck was updated. Refresh the page to open this screen.', 'warning');
      return false;
    }
    const go = await deps.showConfirm(
      heal ? 'This screen needs a reload' : 'DispatchDeck was updated',
      heal
        ? 'DispatchDeck needs a quick refresh to open this screen. It takes a few seconds. Your saved work is safe.'
        : 'This screen needs the new version. Reload to get it — it takes a few seconds. Your saved work is safe.',
      { confirmText: 'Reload', cancelText: 'Not now', icon: 'system_update', iconColor: '#2563EB' }
    );
    if (!go) return false;
    // A reload the user asked for still counts: a second failure of that chunk
    // within 10 minutes goes to the screen instead of asking again (TASK-10108).
    if (heal) writeHeal(d.url);
    // requestAppUpdate asks again if something typed is not saved, and resolves
    // false when the user keeps working.
    return (await deps.reload(d.served)) !== false;
  }

  async function onFail(err) {
    let d;
    try { d = await decide(err); } catch (_) { d = { action: 'rethrow', why: 'decide-threw' }; }
    if (d.action === 'rethrow') throw err;
    try { err.ddStaleChunk = true; } catch (_) {}
    if (state.reloading) return holdForReload(err);
    log(d);
    if (d.action === 'reload' && (d.kind === 'heal' ? writeHeal(d.url) : writeFlag(d.served))) {
      state.reloading = true;
      deps.showToast?.(d.kind === 'heal' ? 'Reloading to open this screen…' : 'DispatchDeck was updated — loading the new version…', 'info');
      let ok = true;
      try { ok = (await deps.reload(d.served)) !== false; } catch (_) { ok = false; }
      if (ok) return holdForReload(err);
      state.reloading = false;
      throw err;
    }
    // Ask. One dialog for every import that fails while it is up; after "Not now",
    // stay quiet for a minute so the screen's retry does not re-open it.
    if (Date.now() - state.declinedAt < DECLINE_QUIET_MS) throw err;
    if (!state.prompting) state.prompting = ask(d).finally(() => { state.prompting = null; });
    let go = false;
    try { go = await state.prompting; } catch (_) { go = false; }
    if (go) { state.reloading = true; return holdForReload(err); }
    state.declinedAt = Date.now();
    throw err;
  }

  // The build calls this with every lazy import()'s promise.
  function wrap(p) {
    if (!p || typeof p.then !== 'function') return p;
    return p.then(undefined, onFail);
  }

  function install(d) { deps = d || null; }

  root.__ddChunk = wrap;
  root.ChunkReload = {
    install, wrap, isChunkLoadError,
    // true while a reload is under way or the question is open (app.js _lazyImportWithRetry)
    busy: () => !!(state.reloading || state.prompting),
    // exposed for the unit test
    _decide: decide, _chunkUrlOf: chunkUrlOf, _state: state, _FLAG_KEY: FLAG_KEY, _HEAL_KEY: HEAL_KEY, _reset() { state.reloading = false; state.prompting = null; state.declinedAt = 0; deps = null; },
  };
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
